<template>
  <div
    class="c-user-select"
    onclick="this.querySelector('input').focus();"
    v-if="ready"
  >
    <multiselect
      :id="id"
      :name="name"
      class="c-form-multiselect"
      :options="filteredUsers"
      :multiple="multiple"
      label="name"
      track-by="id"
      v-model="inputValue"
      :permissions="permissions"
      :taggable="false"
      :custom-label="customLabel"
      placeholder="Select one or more users"
      @search-change="asyncFind"
    >
      <template slot="singleLabel" slot-scope="props">
        <!-- <img class="c-user-select__image" :src="props.option.img" :alt="props.option.name"> -->
        <span class="c-user-select__item">
          <span class="c-user-select__name">{{
            props.option.name || props.option.email
          }}</span>
        </span>
      </template>

      <template slot="noResult"
        >No users found. Please try searching again.</template
      >

      <template slot="option" slot-scope="props">
        <!-- <img class="c-user-select__image" :src="props.option.img" :alt="props.option.name"> -->
        <span class="c-user-select__item" v-if="props.option.name">
          <span class="c-user-select__name">{{ props.option.name }}</span>
          <span class="c-user-select__email">{{ props.option.email }}</span>
        </span>
        <span
          class="c-user-select__item"
          v-else-if="!props.option.name && props.option.email"
        >
          <span class="c-user-select__name">{{ props.option.email }}</span>
          <span class="c-user-select__email">{{ props.option.email }}</span>
        </span>
        <span class="c-user-select__item" v-else>
          <span class="c-user-select__item">{{ props.option.label }}</span>
        </span>
      </template>

      <template slot="tag" slot-scope="{ option, remove }">
        <span class="c-form-multiselect__tag">
          <span>{{ option.name || option.email }}</span>
          <span class="c-form-multiselect__remove" @click="remove(option)">
            <icon name="cross" width="10" height="10"></icon>
          </span>
        </span>
      </template>
    </multiselect>

    <div class="c-form-field__drop-icon">
      <icon name="drop-arrow" width="8" height="8"></icon>
    </div>

    <span class="c-form__validation-message" v-if="error">{{ error }}</span>
  </div>
  <loading v-else />
</template>

<script>
import { mapState } from "vuex";
import debounce from "lodash/debounce";
import UserHasPermission from "@/mixins/UserHasPermission";
export default {
  name: "user-select",

  props: {
    id: [Number, String],
    name: String,
    value: [String, Object, Array, Number],
    error: String,
    permissions: Array,
    multiple: {
      type: Boolean,
      default: false,
    },
  },

  $_veeValidate: {
    value() {
      return this.inputValue;
    },
  },

  mixins: [UserHasPermission],

  data() {
    return {
      ready: false,
      inputValue: this.value,
      isLoading: false,
    };
  },

  created() {
    this.$store
      .dispatch("users/listForSelect")
      .then((resp) => {
        if (this.value) {
          this.users.forEach((user) => {
            user.name = user.name.length > 1 ? user.name : user.email;
            if (user.id === this.value) {
              this.inputValue = user;
            }
          });
          this.ready = true;
        } else {
          this.ready = true;
        }
      })
      .catch((err) => {
        let errMsg;
        if (err.response.data.errors) {
          errMsg = error.response.data.errors[0].messag;
        } else {
          errMsg = err.error.message;
        }
        this.$store.dispatch(
          "notifications/create",
          {
            body: `There was a problem loading the users list.  Please reload the page (${errMsg})`,
            type: "danger",
          },
          { root: true }
        );
      });
  },

  computed: {
    ...mapState({
      users: (state) => state.users.collection,
    }),
    /**
     * If we have any permissions props, check if each user has each permission
     * Only return if they all match
     */
    filteredUsers() {
      if (this.permissions) {
        return this.users.filter((user) => {
          let returnUser = true;
          this.permissions.forEach((perm) => {
            if (!user.privileges[perm]) {
              returnUser = false;
            }
          });

          if (returnUser) {
            // if the user has no name, use the email instead
            return { ...user, name: user.name ? user.name : user.email };
          }
        });
      }

      return this.users;
    },
  },

  methods: {
    asyncFind: debounce(function(query) {
      this.isLoading = true;
      this.$store.dispatch("users/listForSelect", query).then((response) => {
        this.users = response;
        this.isLoading = false;
      });
    }, 500),

    /**
     * Allow us to search by name and email
     */
    customLabel({ name = "", email }) {
      return `${name} - ${email}`;
    },
  },

  watch: {
    inputValue(value) {
      if (value) {
        if (value.name == "") {
          value.name = value.email;
        }
        this.$emit("input", value);
      } else {
        this.$emit("input", null);
      }
    },
  },
};
</script>

<style lang="scss">
.c-form-field__drop-icon {
  position: absolute;

  top: 0;
  right: 0;
  bottom: 0;
  left: auto;

  max-height: 54px;
  width: 3em;

  color: $colour-nhs-dark-grey;
  pointer-events: none;

  display: flex;
  align-items: center;
  justify-content: center;
}

.c-form-field__valid-icon {
  position: absolute;
  right: 0;
  top: 0;
  height: 54px;
  width: 54px;
  padding-top: 18px;
  padding-left: 20px;
  color: #0cce4e;
  pointer-events: none;
}

.multiselect {
  outline: none;
  min-height: 54px;

  * {
    outline: none;
  }

  color: #202020;
  position: relative;

  &__tags {
    padding: 0.5em;
    border-radius: 4px;
    background: $colour-nhs-white;
    border: 2px solid #c9ced4;
    min-height: 54px;
    padding-right: 54px;

    display: flex;
    flex-wrap: wrap;
    align-content: center;
  }

  &--active &__tags {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &--active &__tags,
  &--active &__content-wrapper {
    border-color: $colour-nhs-blue;
  }

  &--active &__content-wrapper {
    border-top: none;
  }

  &__placeholder {
    color: $colour-nhs-mid-grey;
  }

  &__placeholder,
  &__input,
  &__single {
    padding: 0.5em 0.25em;
    margin: 0;
  }

  &__input {
    border: none;
    width: auto;
    padding-left: 0;
    margin-left: 0.25em;
    flex-grow: 1;
    padding: 0;
    max-width: 90%;
  }

  &__tags-wrap {
    display: inline-block;
    margin-top: -6px;
  }

  // div
  &__content-wrapper {
    border: 2px solid #c9ced4;
    margin-top: -2px;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    overflow-y: scroll;

    // min-width: 240px;
    min-width: 100px;

    position: absolute;
    left: 0;
    right: 0;
    z-index: 11;
    background: $colour-nhs-white;
  }

  &.long {
    min-width: 240px;
  }

  // .. ul
  &__content {
    list-style: none;
    padding: 0;
    margin: 0;
    display: block;
    width: 100%;
  }

  &__option {
    padding: 0.5em 1em;
    cursor: pointer;
    display: block;
  }

  &__option--selected {
    color: #c9ced4;

    &:hover {
      background: $colour-nhs-pale-grey;
    }
  }

  &__option:hover:not(.multiselect__option--selected) {
    background: $colour-nhs-pale-grey;
    color: $colour-nhs-blue;
  }

  // group
  &__option--group {
    font-weight: bold;
    background: $colour-nhs-pale-grey;
  }
}

.c-form-multiselect {
  position: relative;
  &__tag,
  .multiselect__tags > .multiselect__strong {
    display: inline-block;
    background: $colour-nhs-dark-grey;
    font-size: 14px;
    color: $colour-nhs-white;
    border-radius: 4px;
    font-weight: normal;
    height: 30px;
  }

  &__tag {
    margin-top: 6px;
    padding: 0.5em 2em 0.5em 0.5em;
    position: relative;
  }

  &__tag:hover {
    background: lighten($colour-nhs-dark-grey, 10%);
  }

  .multiselect__tags > .multiselect__strong {
    padding: 0.5em 0.5em;
  }

  &__tag:not(:last-of-type) {
    margin-right: 0.25em;
  }

  &__tag:last-of-type {
    margin-right: 0.5em;
  }

  &__remove {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    cursor: pointer;
    display: inline-block;
    margin-left: 4px;
    padding: 0.5em;
  }

  &.has-errors .multiselect__tags,
  &.has-errors .multiselect__content-wrapper {
    border-color: $colour-nhs-emergency-red;
  }
}

.c-form-multiselect--ghost {
  padding-right: 20px;
  .multiselect__tags {
    background: none;
    border: none;
  }

  .multiselect__content-wrapper {
    border: none;
    box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.11);
  }
}
.c-user-select {
  position: relative;
  &__item {
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  &__email {
    color: $colour-nhs-mid-grey;
  }
}
</style>
